import React, { useState } from "react";
import { AlertTriangle, ShieldCheck, Download, X, ExternalLink } from "lucide-react";

export const DANGEROUS_EXTS = [
  ".exe", ".dll", ".bat", ".ps1", ".scr", ".msi", ".cmd", ".vbs", ".jar",
  ".zip", ".rar", ".7z", ".apk", ".dmg", ".iso",
];

const BACKEND = "";

export const isDangerousUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase().split("?")[0].split("#")[0];
  return DANGEROUS_EXTS.some((ext) => lower.endsWith(ext));
};

const absUrl = (u) => u;  // same-origin, no prefix needed

const filenameFromUrl = (u) => {
  try {
    const path = (u || "").split("?")[0];
    return decodeURIComponent(path.split("/").pop() || "file");
  } catch (_) {
    return "file";
  }
};

/**
 * Renders a button that opens a hard security warning before allowing the
 * user to download a potentially dangerous file. `verified` softens the look
 * (amber + "less aggressive" copy) but never skips the confirmation.
 */
export const DangerousFileLink = ({
  url,
  label,
  verified = false,
  testId = "dangerous-file-link",
  className = "",
  fullWidth = false,
}) => {
  const [open, setOpen] = useState(false);
  const fname = label || filenameFromUrl(url);
  const accent = verified
    ? { ring: "border-[#ffaa00]/40", bg: "bg-[#ffaa00]/10", text: "text-[#ffaa00]", solid: "bg-[#ffaa00] text-[#06060c]" }
    : { ring: "border-[#ff4444]/50", bg: "bg-[#ff4444]/10", text: "text-[#ff4444]", solid: "bg-[#ff4444] text-white" };

  const confirmDownload = () => {
    setOpen(false);
    // Open in a new tab so the user can see the URL & cancel if needed.
    const a = document.createElement("a");
    a.href = absUrl(url);
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 rounded-lg border ${accent.ring} ${accent.bg} ${accent.text} hover:bg-opacity-20 px-3 py-2 text-sm font-mono ${fullWidth ? "w-full" : ""} ${className}`}
      >
        {verified ? <ShieldCheck size={14} /> : <AlertTriangle size={14} />}
        <span className="truncate flex-1 text-left">{fname}</span>
        <Download size={14} />
      </button>

      {open && (
        <div
          data-testid="dangerous-file-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`cc-elevated rounded-2xl p-6 w-full max-w-md border ${accent.ring}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full ${accent.bg} flex items-center justify-center shrink-0`}>
                {verified ? (
                  <ShieldCheck className={accent.text} size={20} />
                ) : (
                  <AlertTriangle className={accent.text} size={20} />
                )}
              </div>
              <div className="flex-1">
                <div className={`text-xs uppercase tracking-widest font-semibold ${accent.text}`}>
                  {verified ? "Verified company" : "⚠ Unverified file"}
                </div>
                <h3 className="text-lg font-semibold text-white mt-0.5">
                  {verified ? "Always download with care" : "This file could be dangerous"}
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-[#606070] hover:text-white"
                aria-label="Close"
                data-testid="dangerous-file-modal-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3 text-sm text-[#a0a0b0] leading-relaxed">
              <p>
                Only download and run files from companies you trust.{" "}
                <span className="text-white font-medium">CHEATCRAFT is not responsible for damages caused by downloaded files.</span>
              </p>
              <p>
                <span className={`${accent.text} font-medium`}>Never run installers or executables outside of a virtual machine.</span>{" "}
                Scan with your antivirus first and verify the source with the company in chat if anything looks off.
              </p>
              <div className="rounded-lg border border-[#15152a] bg-[#06060c] p-3 font-mono text-[11px] text-[#606070] break-all">
                {absUrl(url)}
              </div>
            </div>

            <div className="flex items-center gap-2 mt-5 justify-end">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm text-[#a0a0b0] hover:text-white"
                data-testid="dangerous-file-cancel"
              >
                Cancel
              </button>
              <a
                href={absUrl(url)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#606070] hover:text-white inline-flex items-center gap-1 px-2"
              >
                <ExternalLink size={12} /> Preview URL
              </a>
              <button
                data-testid="dangerous-file-confirm"
                onClick={confirmDownload}
                className={`rounded-lg px-4 py-2 text-sm font-semibold ${accent.solid}`}
              >
                I understand the risks — Download
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DangerousFileLink;
